import * as React from "react";
// # interfaces
import { iPlan, iPeriod } from "@/interfaces/plans";
import { iPermission } from "@/interfaces/permissions";
import { iApiService } from "@/interfaces/services";
import { iPermissionsService } from "@/interfaces/services/permissions";
// # models
import { PlanModel } from "@/models";
// # hooks
import { useForm, useIsBusy } from "@/hooks";
// # services
import { permissionsService, plansService } from "@/services/api";
// # components
import { ContentEditor } from "@/components/ContentEditor";
import { Spinner, Message } from "@/components/feedback";
import { Tab, Container, Content } from "@/components/tabs";
import { SelectPermissions } from "@/components/permissions";
import { ValidationErrorsList } from "@/components/validation";
import { DeactivatedPlanInfo } from "./";
import { DropZone } from "@/components/DropZone";

export interface iProps {
  /**
   * ID of a plan to edit.
   * A new plan will be created for editing if not provided.
   */
  id?: number;
  /**
   * Function that will be called with the updated plan model after saving.
   */
  onChange?: (updatedPlan: PlanModel) => any;

  permissionsService?: iPermissionsService;
  plansService?: iApiService<PlanModel>;
}

// The model to use for new plans.
export const defaultPlan: iPlan = {
  name: "",
  description: "",
  price: 0,
  permissions: [],
  period: iPeriod.month,
  img: "",
};

/**
 * Allows a plan to be loaded, edited and saved.
 *
 * @type {React.SFC}
 * @param {props: iProps}
 * @returns {React.ReactNode}
 */
const EditPlan: React.SFC<iProps> = (props: iProps) => {
  // Hooks: Load plan from the API, or make a new one.
  const [error, setError] = React.useState(null);
  const planResolver: Promise<PlanModel> = React.useMemo(() => {
    if (props.id) {
      return plansService.find(props.id);
    }
    return Promise.resolve(new PlanModel(defaultPlan));
  }, []);

  // Hooks: form data and validation.
  const { data, setField, isValid, updateFields, validationErrors } =
    useForm<iPlan>({
      rules: {
        name: ["required"],
        description: ["required"],
        price: ["required", "numeric", "min:1"],
        permissions: ["required", "array"],
        period: ["required", "in:month,year"],
        img: ["string"],
      },
    });
  const [showFormErrors, setShowFormErrors] = React.useState(false);

  // Hooks: Load permissions from the API.
  const permissionsResolver = React.useMemo(() => permissionsService.get(), []);
  const [permissions, setPermissions] = React.useState<iPermission[]>([]);

  // Hooks: Manage busy state.
  const isLoading = useIsBusy([planResolver, permissionsResolver]);
  const [isSaving, setIsSaving] = React.useState(false);

  // Hooks: Wait for API responses then update state.
  React.useEffect(() => {
    planResolver
      .then((planModel) => {
        updateFields(planModel.data);
      })
      .catch((err) => {
        setError(err);
        throw err;
      });
    permissionsResolver.then(setPermissions);
  }, []);

  if (isLoading) {
    return <Spinner />;
  }

  if (error) {
    return (
      <Message theme="danger" title="Error loading plan.">
        {error.message}
      </Message>
    );
  }

  if (data.deactivated_at) {
    return <DeactivatedPlanInfo plan={data} />;
  }

  /**
   * Updates the plan price.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} evt
   * @returns {void}
   */
  const handlePriceChange = (
    evt: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const value = parseInt(evt.target.value, 10) * 100;
    setField("price")(value);
  };

  /**
   * Saves the plan.
   *
   * @returns {Promise<void>}
   */
  const saveHandler = async (): Promise<void> => {
    try {
      setShowFormErrors(true);
      if (!isValid) {
        return Promise.resolve();
      }

      setIsSaving(true);
      const updatedPlan: PlanModel = await plansService.save(
        new PlanModel(data),
      );

      updateFields(updatedPlan.data);
      setIsSaving(false);

      if (props.onChange) {
        props.onChange(updatedPlan);
      }
    } catch (err) {
      setIsSaving(false);
    }
  };
  /**
   * Deactivates this plan.
   *
   * @returns {Promise<void>}
   */
  const deactivatePlan = async (): Promise<void> => {
    try {
      setIsSaving(true);
      const updatedPlan = await plansService.delete(data.id);
      updateFields(updatedPlan.data);
      setIsSaving(false);

      if (props.onChange) {
        props.onChange(updatedPlan);
      }
    } catch (err) {
      setIsSaving(false);
      throw err;
    }
  };

  /**
   * Renders a button that saves the plan when clicked.
   *
   * @returns {React.ReactNode}
   */
  const saveButton = (): React.ReactNode => (
    <button className="btn-c2a" disabled={isSaving} onClick={saveHandler}>
      {isSaving ? "Saving" : "Save"}
    </button>
  );

  /**
   * Renders text for period label
   *
   * @returns string
   */
  const getPeriodLabel = (period: iPeriod): string => {
    switch (period) {
      case iPeriod.month:
        return "Month";

      case iPeriod.year:
        return "Year";

      default:
        return period;
    }
  };

  const childToParent = (imageSrc) => {
    data.img = imageSrc;
  };

  return (
    <div>
      <Container initialTab="overview">
        <Tab disabled={isSaving} id="overview">
          Overview
        </Tab>
        <Tab disabled={isSaving} id="benefits">
          Benefits
        </Tab>
        <Tab disabled={isSaving} id="permissions">
          Permissions
        </Tab>
        {data.id ? (
          <Tab disabled={isSaving} id="deactivate" theme="danger">
            Deactivate
          </Tab>
        ) : null}

        <Content id="overview">
          <label htmlFor="plan-name">Title</label>
          <input
            autoFocus
            className="form-control"
            disabled={isSaving}
            name="plan-name"
            onChange={setField("name")}
            placeholder="Plan name"
            type="text"
            value={data.name}
          />

          <div>
            <label htmlFor="plan-image">Image</label>
            <div className="plan-image">
              <DropZone
                childToParent={childToParent}
                parentToChild={data.img}
              />
            </div>
          </div>

          <label htmlFor="plan-period">Period</label>
          <select
            className="form-control"
            disabled={isSaving}
            name="plan-period"
            onChange={setField("period")}
            value={data.period}
          >
            <option value="month">Monthly</option>
            <option value="year">Yearly</option>
          </select>

          <label htmlFor="plan-price">
            Price Per <strong>{getPeriodLabel(data.period)}</strong>
          </label>
          <input
            className="form-control"
            disabled={isSaving}
            name="plan-price"
            onChange={handlePriceChange}
            placeholder="14.99"
            type="number"
            value={(data.price / 100).toFixed(2)}
          />
          {saveButton()}
        </Content>
        <Content id="benefits">
          <label>Benefits</label>
          <ContentEditor
            disabled={isSaving}
            onChange={setField("description")}
            value={data.description}
          />
          {saveButton()}
        </Content>
        <Content id="permissions">
          {permissions ? (
            <SelectPermissions
              permissions={permissions}
              onChange={setField("permissions")}
              value={data.permissions}
            />
          ) : null}
          {saveButton()}
        </Content>
        <Content id="deactivate">
          <Message title="Warning" theme="warning">
            <p>
              Deactivating this plan will prevent new subscriptions being
              created for it.
            </p>
            <p>You may migrate users onto another plan after deactivating.</p>
            <p>
              We recommend letting your users know in advance before migrating
              them to a new plan, especially if the new plan has a different
              price.
            </p>
          </Message>

          <button
            className="btn-c2a style-danger"
            disabled={isSaving}
            onClick={deactivatePlan}
          >
            {isSaving
              ? "Deactivating plan..."
              : `Deactivate "${data.name}" plan`}
          </button>
        </Content>
      </Container>

      {showFormErrors ? (
        <ValidationErrorsList errors={validationErrors.all()} />
      ) : null}
    </div>
  );
};
EditPlan.defaultProps = {
  permissionsService,
  plansService,
};

export default EditPlan;
